// Setup › Chart (CHART-DIMENSIONS, CHART-LENSES; managers): the org chart's company settings on
// the standard Setup frame (56 px LocationHeader, a 1040 px column of `SettingsGroup`s with
// label-left / control-right rows, a SaveDock):
//   - Lenses: the lens a viewer opens on, and who sees each gated lens (the audience levels of
//     CHART-DIMENSIONS: `settings.chart.visibility`, read against the viewer's relation to each
//     seat; Performance is `kpi`, Tasks `load`, Finance `cost`, Overall its own key). Structure
//     and Responsibilities are the floor and have no setting.
//   - Bands: the steps of each scored lens (`settings.chart.lenses.thresholds`); Performance
//     keeps the KPI policy's.
//   - Overall weights (`settings.chart.lenses.weights`, 0–10; 0 leaves a lens out).
//   - Completeness (CHART-KB): which setup items a seat needs to count as complete
//     (`settings.chart.completeness.required`) and whether a digital employee's seat is exempt
//     from the finance item (`de_exempt_finance`). Its audience is fixed (the seat's holders,
//     its line and admins); Execution follows Performance's audience (it reads the scorecard).
//   - Card details: what a Structure card carries by default (`settings.chart.dimensions`).
//   - Founder OS (FOUNDER-OS, an operating company): the head seat a founder's portfolio shows
//     for the company (`settings.head_seat_id`; Automatic = null, the heuristic).
// Written through `PATCH companies/{co}` like Setup › General; keys this form does not show
// survive a save.
import {
  COMPLETENESS_ITEMS,
  type CompletenessItem,
  type CompletenessRules,
  completenessRulesOf,
  type Lens,
  type LensThresholds,
  type LensWeights,
  lensThresholdsOf,
  lensWeightsOf,
  SCORED_LENSES,
  type ScoredLens,
} from "@companyos/domain-work/org";
import {
  Badge,
  Checkbox,
  InfoTip,
  Input,
  Label,
  LensBandDot,
  NativeSelect,
  NativeSelectOption,
  SaveDock,
  SaveIndicator,
  SettingsGroup,
  SettingsRow,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { NetworkIcon } from "lucide-react";
import { type ReactNode, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyDetail } from "../../api/types";
import type { CompanyAccess } from "../company";
import { companyQuery, keys } from "../queries";
import { LoadError, Loading } from "../screen-parts";
import { SetupPage } from "../settings/setup-page";
import {
  DEFAULT_DIMENSIONS,
  DEFAULT_VISIBILITY,
  DIMENSION_HINTS,
  DIMENSION_LABELS,
  DIMENSIONS,
  type Dimension,
  GATED,
  type GatedDimension,
  LEVEL_HINTS,
  LEVEL_LABELS,
  LEVELS,
  type Level,
} from "./chart-dimensions";
import { LENS_GATE, LENS_HINTS, LENS_LABELS, LENSES } from "./chart-lenses";
import { orgChartQuery } from "./chart-queries";
import { COMPLETENESS_HINTS, COMPLETENESS_LABELS } from "./keystones";

type Form = {
  lens: Lens;
  dimensions: Dimension[];
  visibility: Record<GatedDimension, Level>;
  thresholds: LensThresholds;
  weights: LensWeights;
  completeness: CompletenessRules;
  /** FOUNDER-OS: the seat Founder OS shows as this company's head; null = Automatic. */
  headSeat: string | null;
};

function formOf(detail: CompanyDetail): Form {
  const chart = detail.settings.chart;
  const visibility = {} as Record<GatedDimension, Level>;
  for (const g of GATED) visibility[g] = chart?.visibility?.[g] ?? DEFAULT_VISIBILITY[g];
  return {
    lens: chart?.lens ?? "structure",
    dimensions: DIMENSIONS.filter((d) => (chart?.dimensions ?? DEFAULT_DIMENSIONS).includes(d)),
    visibility,
    thresholds: lensThresholdsOf(chart?.lenses?.thresholds),
    weights: lensWeightsOf(chart?.lenses?.weights),
    completeness: completenessRulesOf(chart?.completeness),
    headSeat: detail.settings.head_seat_id ?? null,
  };
}

/** The lenses whose audience is set elsewhere: Execution follows Performance, Completeness is fixed. */
const AUDIENCE_NOTE: Partial<Record<Lens, string>> = {
  execution: "Follows Performance (it reads the same scorecard)",
  completeness: "The seat's holders, the holders above it, and admins",
};

const same = (a: Form, b: Form) => JSON.stringify(a) === JSON.stringify(b);

/** A step is a number from 0 to 300; a weight 0 to 10. */
export const inRange = (n: number, max: number) => Number.isFinite(n) && n >= 0 && n <= max;

/**
 * SEAT-DRAWER-MODALS: the seat panel's Overall and Execution modals write the same lens
 * settings as this page (`settings.chart.lenses`), every other key kept; the chart reads back.
 */
export function useSaveLensSettings(api: Api, co: string) {
  const qc = useQueryClient();
  return useMutation({
    /** Only what changed: merged over the settings as stored now, so another save is kept. */
    mutationFn: async (next: {
      thresholds?: { [K in keyof LensThresholds]?: Partial<LensThresholds[K]> };
      weights?: Partial<LensWeights>;
    }) => {
      const detail = await qc.fetchQuery({ ...companyQuery(api, co), staleTime: 0 });
      const chart = detail.settings.chart;
      // Each band merged step by step: a step not edited keeps the stored value.
      const thresholds = { ...lensThresholdsOf(chart?.lenses?.thresholds) };
      for (const k of Object.keys(next.thresholds ?? {}) as (keyof LensThresholds)[])
        Object.assign(thresholds, { [k]: { ...thresholds[k], ...next.thresholds?.[k] } });
      const weights = { ...lensWeightsOf(chart?.lenses?.weights), ...next.weights };
      // The merged settings must still hold (a step changed elsewhere may cross the one typed).
      if (invalidOf({ ...formOf(detail), thresholds, weights }))
        throw new Error(
          "The chart settings changed elsewhere meanwhile: close and open this again.",
        );
      return api.client.companies.update({
        params: { co },
        body: {
          settings: {
            ...detail.settings,
            chart: {
              ...chart,
              lenses: { thresholds, weights },
            },
          },
        },
      });
    },
    onSuccess: (company) => {
      qc.setQueryData(keys.company(co), company);
      void qc.invalidateQueries({ queryKey: ["company", co, "org-chart"] });
    },
  });
}
function invalidOf(f: Form): boolean {
  const t = f.thresholds;
  const steps = [
    t.responsibilities.green,
    t.responsibilities.amber,
    t.execution.green,
    t.execution.amber,
    t.tasks.green,
    t.tasks.amber,
    t.finance.near,
    t.finance.over,
    t.keystones.green,
    t.keystones.amber,
    t.overall.green,
    t.overall.amber,
  ];
  if (steps.some((s) => !inRange(s, 300))) return true;
  if (SCORED_LENSES.some((l) => !inRange(f.weights[l], 10))) return true;
  return (
    t.responsibilities.green < t.responsibilities.amber ||
    t.execution.green < t.execution.amber ||
    t.tasks.green < t.tasks.amber ||
    t.keystones.green < t.keystones.amber ||
    t.overall.green < t.overall.amber ||
    t.finance.over < t.finance.near
  );
}

export function ChartSettings({ api, company }: { api: Api; company: CompanyAccess }) {
  const detail = useQuery(companyQuery(api, company.id));
  return (
    <SetupPage title="Chart" icon={NetworkIcon} slot="setup-chart">
      {detail.isPending ? (
        <Loading label="Loading chart settings" rows={4} />
      ) : detail.isError ? (
        <LoadError what="Chart settings" error={detail.error} />
      ) : (
        <ChartSettingsForm
          key={detail.data.id}
          api={api}
          detail={detail.data}
          operating={company.kind !== "portfolio"}
        />
      )}
    </SetupPage>
  );
}

/** A 0–300 number field, 72 px wide, with its label for screen readers. */
export function Step({
  id,
  label,
  value,
  max = 300,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  max?: number;
  onChange: (n: number) => void;
}) {
  return (
    <Input
      id={id}
      type="number"
      inputMode="numeric"
      min={0}
      max={max}
      step={1}
      aria-label={label}
      value={Number.isFinite(value) ? value : ""}
      aria-invalid={!inRange(value, max) || undefined}
      onChange={(e) => onChange(e.target.value === "" ? Number.NaN : Number(e.target.value))}
      className="w-[72px] text-right tabular-nums"
    />
  );
}

export function StepPair({ children }: { children: ReactNode }) {
  return (
    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">{children}</span>
  );
}

function ChartSettingsForm({
  api,
  detail,
  operating,
}: {
  api: Api;
  detail: CompanyDetail;
  /** FOUNDER-OS: an operating company pins its head seat (Founder OS has no head of its own). */
  operating: boolean;
}) {
  const qc = useQueryClient();
  const org = useQuery({ ...orgChartQuery(api, detail.id), enabled: operating });
  const headOptions = (org.data?.seats ?? []).filter(
    (s) => s.status === "open" || s.status === "filled",
  );
  const id = useId();
  const [base, setBase] = useState<Form>(() => formOf(detail));
  const [form, setForm] = useState<Form>(base);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const dirty = !same(form, base);
  const invalid = invalidOf(form);

  const save = useMutation({
    mutationFn: () =>
      api.client.companies.update({
        params: { co: detail.id },
        body: {
          settings: {
            ...detail.settings,
            chart: {
              ...detail.settings.chart,
              lens: form.lens,
              dimensions: form.dimensions,
              visibility: form.visibility,
              lenses: { thresholds: form.thresholds, weights: form.weights },
              completeness: form.completeness,
            },
            ...(operating ? { head_seat_id: form.headSeat } : {}),
          },
        },
      }),
    onSuccess: (updated) => {
      // The update answers without the reader's `wallet_view`: keep the read one.
      const company: CompanyDetail = { ...updated, wallet_view: detail.wallet_view };
      qc.setQueryData(keys.company(detail.id), company);
      void qc.invalidateQueries({ queryKey: ["company", detail.id, "org-chart"] });
      const next = formOf(company);
      setBase(next);
      setForm(next);
      setSavedAt(Date.now());
    },
  });

  const toggle = (d: Dimension, on: boolean) =>
    setForm((f) => ({
      ...f,
      dimensions: DIMENSIONS.filter((x) => (x === d ? on : f.dimensions.includes(x))),
    }));
  const setLevel = (d: GatedDimension, level: Level) =>
    setForm((f) => ({ ...f, visibility: { ...f.visibility, [d]: level } }));
  const setStep = <K extends keyof LensThresholds>(
    lens: K,
    step: keyof LensThresholds[K],
    n: number,
  ) =>
    setForm((f) => ({
      ...f,
      thresholds: { ...f.thresholds, [lens]: { ...f.thresholds[lens], [step]: n } },
    }));
  const setWeight = (lens: ScoredLens, n: number) =>
    setForm((f) => ({ ...f, weights: { ...f.weights, [lens]: n } }));
  const setRequired = (item: CompletenessItem, on: boolean) =>
    setForm((f) => ({
      ...f,
      completeness: {
        ...f.completeness,
        required: { ...f.completeness.required, [item]: on },
      },
    }));

  const levelSelect = (gate: GatedDimension, selectId: string) => (
    <>
      <NativeSelect
        id={selectId}
        value={form.visibility[gate]}
        onChange={(e) => setLevel(gate, e.target.value as Level)}
        className="w-56 max-md:w-full"
      >
        {LEVELS.map((l) => (
          <NativeSelectOption key={l} value={l}>
            {LEVEL_LABELS[l]}
          </NativeSelectOption>
        ))}
      </NativeSelect>
      <span className="w-52 text-xs text-faint max-lg:hidden">
        {LEVEL_HINTS[form.visibility[gate]]}
      </span>
    </>
  );

  return (
    <>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!invalid) save.mutate();
        }}
      >
        {operating ? (
          <SettingsGroup id="founder-os" title="Founder OS">
            <SettingsRow
              label={
                <span className="inline-flex items-center gap-1">
                  Head seat
                  <InfoTip label="About the head seat">
                    The seat Founder OS shows for this company. Automatic: the filled top seat with
                    the most reports.
                  </InfoTip>
                </span>
              }
              htmlFor={`${id}-head-seat`}
            >
              <NativeSelect
                id={`${id}-head-seat`}
                value={form.headSeat ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, headSeat: e.target.value || null }))}
                className="w-56 max-md:w-full"
                data-slot="head-seat"
              >
                <NativeSelectOption value="">Automatic</NativeSelectOption>
                {headOptions.map((s) => (
                  <NativeSelectOption key={s.id} value={s.id}>
                    {s.title}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </SettingsRow>
          </SettingsGroup>
        ) : null}
        <SettingsGroup id="lenses" title="Lenses">
          <SettingsRow
            label="Opens on"
            hint="The lens a viewer sees first, until they pick their own."
            htmlFor={`${id}-lens`}
          >
            <NativeSelect
              id={`${id}-lens`}
              value={form.lens}
              onChange={(e) => setForm((f) => ({ ...f, lens: e.target.value as Lens }))}
              className="w-56 max-md:w-full"
              data-slot="default-lens"
            >
              {LENSES.map((l) => (
                <NativeSelectOption key={l} value={l}>
                  {LENS_LABELS[l]}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </SettingsRow>
          {LENSES.map((l) => {
            const note = AUDIENCE_NOTE[l];
            const gate = note ? undefined : LENS_GATE[l];
            const selectId = `${id}-level-${l}`;
            return (
              <SettingsRow
                key={l}
                data-lens={l}
                data-slot="lens-visibility"
                label={
                  <span className="flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      data-lens={l}
                      className="size-2.5 rounded-[2px] bg-(--lens)"
                    />
                    {LENS_LABELS[l]}
                    {!gate && !note ? <Badge variant="outline">Floor</Badge> : null}
                  </span>
                }
                hint={LENS_HINTS[l]}
                {...(gate ? { htmlFor: selectId } : {})}
              >
                {gate ? (
                  levelSelect(gate, selectId)
                ) : (
                  <span className="text-xs text-muted-foreground">
                    {note ?? "Everyone who opens the chart"}
                  </span>
                )}
              </SettingsRow>
            );
          })}
        </SettingsGroup>

        <SettingsGroup id="bands" title="Bands">
          <SettingsRow
            label="Responsibilities · coverage"
            hint="Share of a seat's responsibilities with measurables, in %."
            data-slot="steps-responsibilities"
          >
            <StepPair>
              <LensBandDot band="green" /> ≥
              <Step
                id={`${id}-resp-green`}
                label="Responsibilities: green at or above"
                value={form.thresholds.responsibilities.green}
                onChange={(n) => setStep("responsibilities", "green", n)}
              />
              <LensBandDot band="amber" /> ≥
              <Step
                id={`${id}-resp-amber`}
                label="Responsibilities: amber at or above"
                value={form.thresholds.responsibilities.amber}
                onChange={(n) => setStep("responsibilities", "amber", n)}
              />
              <LensBandDot band="red" /> below
            </StepPair>
          </SettingsRow>
          <SettingsRow
            label="Performance · scorecard band"
            hint="The seat scorecard's own bands (the KPI policy): great and on track are green, average amber, low red."
            data-slot="steps-performance"
          >
            <span className="text-xs text-muted-foreground">Set in the KPI policy</span>
          </SettingsRow>
          <SettingsRow
            label="Execution · goal score"
            hint="The seat scorecard's goal score: its weighted target attainment, 0–100."
            data-slot="steps-execution"
          >
            <StepPair>
              <LensBandDot band="green" /> ≥
              <Step
                id={`${id}-exec-green`}
                label="Execution: green at or above"
                value={form.thresholds.execution.green}
                onChange={(n) => setStep("execution", "green", n)}
              />
              <LensBandDot band="amber" /> ≥
              <Step
                id={`${id}-exec-amber`}
                label="Execution: amber at or above"
                value={form.thresholds.execution.amber}
                onChange={(n) => setStep("execution", "amber", n)}
              />
              <LensBandDot band="red" /> below
            </StepPair>
          </SettingsRow>
          <SettingsRow
            label="Tasks · on time"
            hint="100 minus the overdue share of a seat's open tasks."
            data-slot="steps-tasks"
          >
            <StepPair>
              <LensBandDot band="green" /> ≥
              <Step
                id={`${id}-tasks-green`}
                label="Tasks: green at or above"
                value={form.thresholds.tasks.green}
                onChange={(n) => setStep("tasks", "green", n)}
              />
              <LensBandDot band="amber" /> ≥
              <Step
                id={`${id}-tasks-amber`}
                label="Tasks: amber at or above"
                value={form.thresholds.tasks.amber}
                onChange={(n) => setStep("tasks", "amber", n)}
              />
              <LensBandDot band="red" /> below
            </StepPair>
          </SettingsRow>
          <SettingsRow
            label="Finance · budget used"
            hint="Actual over budget this month, in %."
            data-slot="steps-finance"
          >
            <StepPair>
              <LensBandDot band="amber" /> from
              <Step
                id={`${id}-fin-near`}
                label="Finance: amber from"
                value={form.thresholds.finance.near}
                onChange={(n) => setStep("finance", "near", n)}
              />
              <LensBandDot band="red" /> above
              <Step
                id={`${id}-fin-over`}
                label="Finance: red above"
                value={form.thresholds.finance.over}
                onChange={(n) => setStep("finance", "over", n)}
              />
              %
            </StepPair>
          </SettingsRow>
          <SettingsRow
            label="Priorities · on track"
            hint="Share of the quarter's priorities on track, in %; one at risk counts half."
            data-slot="steps-keystones"
          >
            <StepPair>
              <LensBandDot band="green" /> ≥
              <Step
                id={`${id}-keys-green`}
                label="Priorities: green at or above"
                value={form.thresholds.keystones.green}
                onChange={(n) => setStep("keystones", "green", n)}
              />
              <LensBandDot band="amber" /> ≥
              <Step
                id={`${id}-keys-amber`}
                label="Priorities: amber at or above"
                value={form.thresholds.keystones.amber}
                onChange={(n) => setStep("keystones", "amber", n)}
              />
              <LensBandDot band="red" /> below
            </StepPair>
          </SettingsRow>
          <SettingsRow
            label="Completeness · seat setup"
            hint="Share of the required setup items in place on the seat."
            data-slot="steps-completeness"
          >
            <StepPair>
              <LensBandDot band="green" /> complete
              <LensBandDot band="amber" /> ≥ 80%
              <LensBandDot band="red" /> below
            </StepPair>
          </SettingsRow>
          <SettingsRow
            label="Overall"
            hint="The weighted combination, 0–100."
            data-slot="steps-overall"
          >
            <StepPair>
              <LensBandDot band="green" /> ≥
              <Step
                id={`${id}-overall-green`}
                label="Overall: green at or above"
                value={form.thresholds.overall.green}
                onChange={(n) => setStep("overall", "green", n)}
              />
              <LensBandDot band="amber" /> ≥
              <Step
                id={`${id}-overall-amber`}
                label="Overall: amber at or above"
                value={form.thresholds.overall.amber}
                onChange={(n) => setStep("overall", "amber", n)}
              />
              <LensBandDot band="red" /> below
            </StepPair>
          </SettingsRow>
        </SettingsGroup>

        <SettingsGroup id="weights" title="Overall weights">
          {SCORED_LENSES.map((l) => (
            <SettingsRow
              key={l}
              label={LENS_LABELS[l]}
              hint={
                l === "finance"
                  ? "Budget used counts as a goodness: 100 up to amber, 50 at red."
                  : LENS_HINTS[l]
              }
              htmlFor={`${id}-weight-${l}`}
              data-slot="lens-weight"
              data-lens={l}
            >
              <StepPair>
                ×
                <Step
                  id={`${id}-weight-${l}`}
                  label={`${LENS_LABELS[l]} weight`}
                  value={form.weights[l]}
                  max={10}
                  onChange={(n) => setWeight(l, n)}
                />
                <span className="w-24 text-faint">
                  {form.weights[l] === 0 ? "left out" : "0–10"}
                </span>
              </StepPair>
            </SettingsRow>
          ))}
        </SettingsGroup>

        {/* CHART-KB: what a seat needs to count as set up ("Seat setup: 78% complete"). */}
        <SettingsGroup id="completeness" title="Seat completeness">
          {COMPLETENESS_ITEMS.map((item) => {
            const inputId = `${id}-req-${item}`;
            return (
              <SettingsRow
                key={item}
                label={COMPLETENESS_LABELS[item]}
                hint={COMPLETENESS_HINTS[item]}
                htmlFor={inputId}
                data-slot="completeness-item"
                data-item={item}
              >
                <span className="flex items-center gap-2">
                  <Checkbox
                    id={inputId}
                    checked={form.completeness.required[item]}
                    onCheckedChange={(on) => setRequired(item, on === true)}
                    aria-label={`${COMPLETENESS_LABELS[item]}: required`}
                  />
                  <Label htmlFor={inputId} className="text-xs font-normal">
                    Required for a complete seat
                  </Label>
                </span>
              </SettingsRow>
            );
          })}
          <SettingsRow
            label="Digital employees and finance"
            hint="A seat held by a digital employee needs no budget or cost line (its cost comes from its subscription)."
            htmlFor={`${id}-de-exempt`}
            data-slot="completeness-de-exempt"
          >
            <span className="flex items-center gap-2">
              <Checkbox
                id={`${id}-de-exempt`}
                checked={form.completeness.de_exempt_finance}
                onCheckedChange={(on) =>
                  setForm((f) => ({
                    ...f,
                    completeness: { ...f.completeness, de_exempt_finance: on === true },
                  }))
                }
                aria-label="Digital employees exempt from the finance item"
              />
              <Label htmlFor={`${id}-de-exempt`} className="text-xs font-normal">
                Exempt from the finance item
              </Label>
            </span>
          </SettingsRow>
        </SettingsGroup>

        <SettingsGroup id="card-details" title="Card details">
          {DIMENSIONS.map((d) => {
            const inputId = `${id}-dim-${d}`;
            return (
              <SettingsRow
                key={d}
                label={
                  <span className="flex items-center gap-1.5">
                    {DIMENSION_LABELS[d]}
                    {d === "team" || d === "responsibilities" ? (
                      <Badge variant="outline">Floor</Badge>
                    ) : null}
                  </span>
                }
                hint={DIMENSION_HINTS[d]}
                htmlFor={inputId}
                data-slot="default-dimension"
                data-dimension={d}
              >
                <span className="flex items-center gap-2">
                  <Checkbox
                    id={inputId}
                    checked={form.dimensions.includes(d)}
                    onCheckedChange={(on) => toggle(d, on === true)}
                    aria-label={DIMENSION_LABELS[d]}
                  />
                  <Label htmlFor={inputId} className="text-xs font-normal">
                    On a Structure card by default
                  </Label>
                </span>
              </SettingsRow>
            );
          })}
        </SettingsGroup>
      </form>
      <SaveIndicator savedAt={savedAt} />
      <SaveDock
        dirty={dirty}
        saving={save.isPending}
        onSave={() => {
          if (!invalid) save.mutate();
        }}
        onDiscard={() => setForm(base)}
        message={
          save.isError
            ? "Could not save. Try again."
            : invalid
              ? "Steps are 0–300 with green at or above amber (red above amber for finance); weights 0–10"
              : undefined
        }
      />
    </>
  );
}
